import { Component, EventEmitter, Output, inject, viewChild, OnDestroy, OnInit, AfterViewInit, ChangeDetectorRef} from '@angular/core';
import { CommonModule } from '@angular/common';
import { lastValueFrom, of, Subscription } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged } from 'rxjs/operators';
import { ReceiptDatasourceService } from '../receipt-datasource.service';
import { CampaignsReceiptAdminApiClient, IReceiptQuery, Receipt } from 'src/app/api/api-campaigns.service.generated';
import { ReceiptFiltersComponent } from '../receipt-filters/receipt-filters.component';
import { ColumnData, ConfirmDialogModel } from 'wecheer-common';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DatatableComponent, SelectionType } from '@swimlane/ngx-datatable';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { RECEIPT_TAGS } from 'src/app/const/receipt-tag';
import { MatChipsModule } from '@angular/material/chips';
import { ReceiptPanelComponent } from '../receipt-details/receipt-panel/receipt-panel.component';
import { formatPhoneNumber } from 'src/app/common/utils/phone-formatter.util';
import { UserPermissionsService } from 'src/app/common/services/user-permissions.service';

@Component({
  selector: 'app-receipt-grid',
  templateUrl: './receipt-grid.component.html',
  styleUrls: ['./receipt-grid.component.scss'],
  imports: [
    NgxDatatableModule,
    ReceiptFiltersComponent,
    ReceiptPanelComponent,
    MatChipsModule,
    CommonModule,
    MatIconModule,
    MatButtonModule
  ]
})
export class ReceiptGridComponent implements OnInit, OnDestroy, AfterViewInit {
  private datasource = inject(ReceiptDatasourceService);
  private receiptApi = inject(CampaignsReceiptAdminApiClient);
  private dialog = inject(MatDialog);
  private snackbar = inject(MatSnackBar);
  private userPermissionsService = inject(UserPermissionsService);
  private cdr = inject(ChangeDetectorRef);

  @Output() receiptSelected = new EventEmitter<Receipt>();
  @Output() rowHovered = new EventEmitter<number>();
  @Output() rowClicked = new EventEmitter<number>();
  @Output() rowCtrlClicked = new EventEmitter<Receipt>();

  readonly filtersPanel = viewChild<ReceiptFiltersComponent>('filtersPanel');
  readonly datatable = viewChild<DatatableComponent>('datatable');
  readonly detailPanel = viewChild<ReceiptPanelComponent>('detailPanel');

  readonly pageSize = 25;
  rowHeight = 40;
  SelectionType = SelectionType; // Expose SelectionType enum to template

  private columnChangeSubscription: Subscription;
  private dataSubscription: Subscription | null = null;
  configColumns: ColumnData[] = [];

  // Cache for receipt stages from user activity
  private stageCache = new Map<string, string>();
  private loadingStages = new Set<string>(); // Track receipts currently loading

  columns: ColumnData[] = [
    { key: 'stage', label: 'Stage', colWidth: 200, displayLabelHandler: (receipt: Receipt) => this.getStage(receipt), sortable: true, onlyIcon: true },
    { key: 'decision', label: 'Decision', colWidth: 100, displayLabelHandler: (receipt: Receipt) => this.getDecisionWithStyling(receipt), customClass: 'decision-cell', sortable: true, onlyIcon: true },
    // { key: 'receiptUrl', label: 'Preview', sortable: false, colWidth: 100, format: { type: 'image', showNewStatusByKey: 'isNew', allowOpenImage: true } },
    { key: 'createDate', label: 'Scan Date', sortable: true, colWidth: 220, displayLabelHandler: (receipt: Receipt) => this.getDateWithNewIndicator(receipt), onlyIcon: true },
    { key: 'userPhoneNo', label: 'User Phone', sortable: true, displayLabelHandler: (receipt: Receipt) => this.highlightText(formatPhoneNumber(receipt.userPhoneNo)), onlyIcon: true },
    { key: 'docId', label: 'Verify Doc ID', sortable: true, displayLabelHandler: (receipt: Receipt) => this.highlightText(receipt.docId), onlyIcon: true },
    { key: 'invoiceDate', label: 'Invoice date', sortable: true, format: { type: 'date' } },
    { key: 'invoiceNumber', label: 'Invoice No', sortable: true },
    { key: 'referenceNumber', label: 'Reference No', sortable: false },
    { key: 'vendor.name', label: 'Vendor', sortable: true },
    { key: 'campaignName', label: 'Campaign Name', sortable: true, displayLabelHandler: (receipt: Receipt) => this.highlightText(receipt.campaignName), onlyIcon: true },
    // { key: 'campaignId', label: 'Campaign ID', sortable: true },
    { key: 'suspectDuplicateGroup', label: 'Suspect group', sortable: true },
    { key: 'tags', label: 'Tags', colWidth: 300, displayLabelHandler: (receipt: Receipt) => this.getTags(receipt), sortable: false, onlyIcon: true },
    { key: 'note', label: 'Admin Note', sortable: true },
    { key: 'meta.fraud.attribution', label: 'Reason', sortable: true },
    { key: 'meta.fraud.decision', label: 'Fraud Decision', sortable: true },
    { key: 'meta.fraud.types', label: 'Types', sortable: true },
    { key: 'meta.warnings', label: 'Warnings', displayLabelHandler: (receipt: Receipt) => this.getWarnings(receipt), sortable: true },
    // {
    //   key: 'actionButtons',
    //   label: 'Actions',
    //   sortable: false,
    //   colWidth: 120,
    //   actionButtons: [
    //     { icon: 'visibility', tooltip: 'View', handler: (receipt: Receipt) => this.onReceiptSelected(receipt) },
    //     { icon: 'delete', tooltip: 'Delete', visible: this.checkPermissions([PERMISSION.CAMPAIGNMANAGEMENT_WRITE, PERMISSION.CAMPAIGN_DELETE]), handler: (receipt: Receipt) => this.onReceiptDelete(receipt) }
    //   ]
    // }
  ];

  relevantTags = ['ToReprocess', 'ToCheckFraud', 'ManualAccept', 'ManualReject', 'ToManualVerify', 'ReprocessSuccess', 'ReprocessFail', 'Error', 'Checkby', 'ReceivedStamps', 'ReceivedPoints'];
  tagToStageMap: { [key: string]: string } = {
    [RECEIPT_TAGS.NOT_ENOUGH_STAMPS]: 'Not enough stamps',
    [RECEIPT_TAGS.STAMPS_RECEIVED_TAG]: 'Eligible',
    [RECEIPT_TAGS.POINTS_RECEIVED_TAG]: 'Eligible',
    [RECEIPT_TAGS.NOT_RECOGNIZABLE_TAG]: 'Not recognizable',
    [RECEIPT_TAGS.VERIFY_DUPLICATE_TAG]: 'Verify duplication',
    [RECEIPT_TAGS.SUSPECT_DUPLICATE_TAG]: 'Suspect duplicated',
    [RECEIPT_TAGS.SUSPECT_LOCATION_TAG]: 'Suspect location',
    [RECEIPT_TAGS.TIME_CONSTRAINT_NOT_VALID]: 'Time constraint not valid',
    [RECEIPT_TAGS.CURRENCY_CONSTRAINT_NOT_PASS]: 'Currency constraint not pass',
    [RECEIPT_TAGS.NOT_ELIGIBLE_TAG]: 'Not eligible',
    [RECEIPT_TAGS.INCOMPLETE_TAG]: 'Incomplete',
    [RECEIPT_TAGS.MANUAL_VERIFY_TAG]: 'Forced manual review',
    [RECEIPT_TAGS.CONSUMPTION_LIMIT_REACHED_TAG]: 'Consumption limit reached',
    [RECEIPT_TAGS.POINTS_LIMIT_REACHED_TAG]: 'Points limit reached',
    [RECEIPT_TAGS.STAMPS_LIMIT_REACHED_TAG]: 'Stamps limit reached',
    [RECEIPT_TAGS.PUNCHCARD_ALREADY_COMPLETED_TAG]: 'Punchcard completed',
    [RECEIPT_TAGS.MANUAL_REVIEW_LIMIT_EXCEEDED_TAG]: 'Review limit reached'
  };
  customOrder = [
    'Not recognizable',
    'Verify duplication',
    'Suspect duplicated',
    'Rejected',
    'Suspect location',
    'Time constraint not valid',
    'Currency constraint not pass',
    'Not eligible',
    'Incomplete',
    'Not enough stamps',
    'Eligible',
    'Forced manual review'
  ];

  selectedReceipt: Receipt;
  filter: IReceiptQuery;

  receiptsList: Receipt[];
  isFirstRequest: boolean;
  isLoading: boolean;

  // Pagination state
  currentPage = 1;
  totalPages = 1;
  totalItems = 0;

  // Group expansion state
  expandedGroups = new Set<string>(); // Track which docId groups are expanded
  groupedReceipts: Map<string, Receipt[]> = new Map(); // Map of docId to receipts
  displayReceipts: Receipt[] = []; // Flattened list for display
  navigableRows: Receipt[] = []; // Only group headers and single rows (for keyboard nav)

  // Active row tracking for focus/highlighting
  private lastHoveredNavIndex = -1;
  private normalizedRelevantTagKeywords: string[] = [];

  // Sorting state
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  constructor() {
    this.normalizedRelevantTagKeywords = this.relevantTags.map(tag => tag.toLowerCase());
  }

  ngOnInit(): void {
    // Subscribe to column changes
    this.setupColumnChangeListener();

    // Subscribe to data changes from datasource
    this.dataSubscription = this.datasource.pageAvailable.subscribe((receipts) => {
      this.receiptsList = receipts;
      this.isFirstRequest = this.datasource.isFirstRequest;
      this.isLoading = this.datasource.isLoading;
      this.totalItems = this.datasource.total || 0;
      this.updatePagination();
      this.groupReceiptsByDocId();

      // Load stages for receipts with unknown stage
      this.loadUnknownStages(receipts);

      // Re-attach row event listeners when data changes
      setTimeout(() => {
        this.attachRowEventListeners();
      }, 100);
    });
  }

  ngAfterViewInit(): void {
    // Add mouse event listeners to datatable rows after view init
    setTimeout(() => {
      this.attachRowEventListeners();
    }, 500);
  }

  private attachRowEventListeners(): void {
    const rows = document.querySelectorAll('.datatable-body-row');
    rows.forEach((rowElement, domIndex) => {
      const element = rowElement as HTMLElement;
      element.dataset.displayIndex = domIndex.toString();

      if (element.dataset.listenerBound !== 'true') {
        element.dataset.listenerBound = 'true';

        // Mouse enter - track hovered row
        element.addEventListener('mouseenter', () => {
          const displayIndex = Number(element.dataset.displayIndex ?? '-1');
          const rowData = this.displayReceipts?.[displayIndex] as any;
          if (!rowData || rowData._isGroupChild) {
            return;
          }

          const navIndex = this.getNavigableIndexForRow(rowData);
          if (navIndex !== -1 && navIndex !== this.lastHoveredNavIndex) {
            this.lastHoveredNavIndex = navIndex;
            this.rowHovered.emit(navIndex);
          }
        });

        // Click - select row and trigger detail panel
        element.addEventListener('click', (e) => {
          const displayIndex = Number(element.dataset.displayIndex ?? '-1');
          const rowData = this.displayReceipts?.[displayIndex] as any;
          if (!rowData) {
            return;
          }

          // Check if click was on expand button or its children
          const target = e.target as HTMLElement;
          if (target.closest('.expand-button-wrapper') || target.closest('.expand-cell')) {
            return; // Don't activate row if clicking expand button
          }

          const mouseEvent = e as MouseEvent;

          if (mouseEvent.ctrlKey || mouseEvent.metaKey) {
            mouseEvent.preventDefault();
            mouseEvent.stopPropagation();

            const receiptToOpen = this.getReceiptForCtrlClick(rowData);
            if (receiptToOpen) {
              this.rowCtrlClicked.emit(receiptToOpen);
            }
            return;
          }

          e.stopPropagation();

          if (rowData._isGroupChild) {
            return;
          }

          const navIndex = this.getNavigableIndexForRow(rowData);
          if (navIndex !== -1) {
            this.rowClicked.emit(navIndex);
            this.lastHoveredNavIndex = navIndex;
          }

          this.onActivate(rowData);
        });
      } else {
        // Listener already attached, ensure display index stays in sync
        element.dataset.displayIndex = domIndex.toString();
      }
    });
  }

  private setupColumnChangeListener() {
    this.columnChangeSubscription = this.datasource.checkedColumnsChange$
      .pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        debounceTime(100)
      )
      .subscribe((checkedColumns) => {
        this.getConfigColumns(checkedColumns);
      });
  }

  ngOnDestroy() {
    if (this.columnChangeSubscription) {
      this.columnChangeSubscription.unsubscribe();
    }
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
  }

  private groupReceiptsByDocId(): void {
    if (!this.receiptsList || this.receiptsList.length === 0) {
      this.displayReceipts = [];
      return;
    }

    // Clear previous grouping
    this.groupedReceipts.clear();
    const newDisplayReceipts: Receipt[] = [];

    // Group receipts by docId
    this.receiptsList.forEach(receipt => {
      if (!receipt) return; // Safety check

      const docId = receipt.docId || `no-docid-${receipt.id}`;
      if (!this.groupedReceipts.has(docId)) {
        this.groupedReceipts.set(docId, []);
      }
      this.groupedReceipts.get(docId)!.push(receipt);
    });

    // Build display list with grouping support
    this.groupedReceipts.forEach((receipts, docId) => {
      const groupSize = receipts.length;
      const isExpanded = this.expandedGroups.has(docId);

      // Count unique campaigns in this group
      const uniqueCampaigns = new Set(receipts.map(r => r.campaignName).filter(name => name));
      const campaignCount = uniqueCampaigns.size;

      if (groupSize === 1) {
        // If only one receipt, show it as a regular row (no grouping)
        const singleReceipt: any = {
          ...receipts[0]
        };
        newDisplayReceipts.push(singleReceipt);
      } else {
        // Multiple receipts - show as group
        // Add the first receipt as the group header
        const firstReceipt: any = {
          ...receipts[0],
          _isGroupHeader: true,
          _groupSize: groupSize,
          _docId: docId,
          _isExpanded: isExpanded,
          _campaignCount: campaignCount
        };
        newDisplayReceipts.push(firstReceipt);

        // When expanded, show ALL receipts as child rows
        if (isExpanded) {
          for (let i = 0; i < receipts.length; i++) {
            const childReceipt: any = {
              ...receipts[i],
              _isGroupChild: true,
              _docId: docId
            };
            newDisplayReceipts.push(childReceipt);
          }
        } else {
          // Group collapsed, no child rows rendered
        }
      }
    });

    // Update display receipts
    this.displayReceipts = newDisplayReceipts;

    // Build navigable rows list (only group headers and single rows, no children)
    this.navigableRows = this.displayReceipts.filter(r => {
      const row = r as any;
      return !row._isGroupChild; // Exclude child rows
    });

  }

  // Get count of navigable rows (for keyboard navigation)
  getNavigableRowCount(): number {
    return this.navigableRows.length;
  }

  // Find the navigable index for a given row (group header or single row)
  getNavigableIndexForRow(row: any): number {
    const target = row as any;
    return this.navigableRows.findIndex(r => {
      const nav = r as any;
      return nav.id === target.id && (!!nav._isGroupHeader === !!target._isGroupHeader);
    });
  }

  isGroupExpanded(docId: string): boolean {
    return this.expandedGroups.has(docId);
  }

  onExpandButtonClick(event: MouseEvent, docId: string): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.toggleGroup(docId);
  }

  onRowCellClick(row: any): void {
    // Don't allow clicks on child rows
    if (row._isGroupChild) {
      return;
    }

    const navIndex = this.getNavigableIndexForRow(row);
    if (navIndex !== -1) {
      this.rowClicked.emit(navIndex);
      this.lastHoveredNavIndex = navIndex;
    }

    // If it's a group header row, activate it and mark as read if needed
    if (row._isGroupHeader) {
      if (row.isNew) {
        this.markReceiptAsRead(row);
      }
      // Open detail panel for group header
      this.onActivate(row);
      return;
    }

    // For regular (non-grouped) rows, proceed with normal activation
    this.onActivate(row);
  }

  toggleGroup(docId: string): void {
    if (this.expandedGroups.has(docId)) {
      this.expandedGroups.delete(docId);
    } else {
      this.expandedGroups.add(docId);
    }

    // Rebuild display list
    this.groupReceiptsByDocId();

    // Force complete re-render
    const dt = this.datatable();
    if (dt) {
      // Method 1: Set rows to empty first, then set to new data
      dt.rows = [];

      setTimeout(() => {
        dt.rows = this.displayReceipts;
        dt.recalculate();

        // Re-attach event listeners after DOM updates
        setTimeout(() => {
          this.attachRowEventListeners();
        }, 100);
      }, 10);
    } else {
      console.warn('Datatable instance not found during toggleGroup');
    }
  }

  expandAllGroups(): void {
    this.groupedReceipts.forEach((_, docId) => {
      this.expandedGroups.add(docId);
    });
    this.groupReceiptsByDocId();
    this.displayReceipts = [...this.displayReceipts];
    setTimeout(() => {
      this.cdr.detectChanges();
      const table = this.datatable();
      if (table) {
        table.recalculate();
      }
      setTimeout(() => {
        this.attachRowEventListeners();
      }, 100);
    }, 0);
  }

  areAllGroupsExpanded(): boolean {
    if (this.groupedReceipts.size === 0) {
      return false;
    }
    return this.expandedGroups.size === this.groupedReceipts.size;
  }

  private getReceiptForCtrlClick(rowData: any): Receipt | null {
    if (!rowData) {
      return null;
    }

    if (rowData._isGroupHeader) {
      const docId = rowData._docId as string | undefined;
      if (!docId) {
        return rowData as Receipt;
      }
      const groupReceipts = this.groupedReceipts.get(docId);
      if (groupReceipts && groupReceipts.length > 0) {
        return groupReceipts[0];
      }
      return rowData as Receipt;
    }

    return rowData as Receipt;
  }

  collapseAllGroups(): void {
    this.expandedGroups.clear();
    this.groupReceiptsByDocId();
    this.displayReceipts = [...this.displayReceipts];
    setTimeout(() => {
      this.cdr.detectChanges();
      const table = this.datatable();
      if (table) {
        table.recalculate();
      }
      setTimeout(() => {
        this.attachRowEventListeners();
      }, 100);
    }, 0);
  }

  // Helper method to get nested property values
  getValue(row: any, key: string): any {
    const keys = key.split('.');
    let value = row;
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return undefined;
      }
    }
    return value;
  }

  // Sort handler
  onSort(event: any): void {
    if (!event || !event.sorts || event.sorts.length === 0) {
      return;
    }

    const sort = event.sorts[0];
    this.sortColumn = sort.prop;
    this.sortDirection = sort.dir;

    // Sort the receiptsList (source data)
    this.receiptsList.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      // Special handling for columns with display handlers
      if (this.sortColumn === 'stage') {
        aVal = this.getStageValue(a);
        bVal = this.getStageValue(b);
      } else if (this.sortColumn === 'decision') {
        aVal = this.getDecision(a);
        bVal = this.getDecision(b);
      } else if (this.sortColumn === 'createDate' || this.sortColumn === 'invoiceDate') {
        // Handle date sorting
        aVal = this.getValue(a, this.sortColumn);
        bVal = this.getValue(b, this.sortColumn);
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else {
        // Default: get value by key
        aVal = this.getValue(a, this.sortColumn);
        bVal = this.getValue(b, this.sortColumn);
      }

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Compare values
      let comparison = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.toLowerCase().localeCompare(bVal.toLowerCase());
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    // Rebuild grouped receipts with sorted data
    this.groupReceiptsByDocId();

    // Force table refresh
    const dt = this.datatable();
    if (dt) {
      dt.rows = [];
      setTimeout(() => {
        dt.rows = this.displayReceipts;
        dt.recalculate();
        setTimeout(() => {
          this.attachRowEventListeners();
        }, 100);
      }, 10);
    }
  }

  // Helper method to get row CSS class
  getRowClass = (row: any): any => {
    return {
      'group-header-row': row._isGroupHeader,
      'group-child-row': row._isGroupChild
    };
  }

  // Helper method to format phone numbers
  formatPhoneNumber(phone: string): string {
    return formatPhoneNumber(phone);
  }

  getConfigColumns(checkedColumns: string[]) {
    const keyMap = {
      scanDate: 'createDate',
      userPhone: 'userPhoneNo',
      docId: 'docId',
      invoiceDate: 'invoiceDate',
      invoiceNumber: 'invoiceNumber',
      referenceNumber: 'referenceNumber',
      vendor: 'vendor.name',
      campaignName: 'campaignName',
      campaignId: 'campaignId',
      stage: 'stage',
      suspectDuplicateGroup: 'suspectDuplicateGroup',
      tags: 'tags',
      decision: 'decision',
      adminNote: 'note',
      reason: 'meta.fraud.attribution',
      types: 'meta.fraud.types',
      warnings: 'meta.warnings',
    };

    // Filter columns based on checked columns
    const filtered: ColumnData[] = [];

    this.columns.forEach(col => {
      // Check if this column is in the checked list
      const isChecked = checkedColumns.some(name => keyMap[name] === col.key);
      if (isChecked) {
        filtered.push(col);
      }
    });

    this.configColumns = filtered;
    return this.configColumns;
  }

  onReceiptSelected(receipt: Receipt) {
    this.selectedReceipt = receipt;
    this.receiptSelected.emit(receipt);
  }

  async onReceiptDelete(receipt: Receipt) {
    const dialogModel = ConfirmDialogModel.danger(
      'This action cannot be undone. Are you absolutely sure ? ',
      'Delete Receipt?'
    );
    const confirmResult = await dialogModel.show(this.dialog);
    if (!confirmResult) return;

    await lastValueFrom(this.receiptApi.deleteReceiptById(receipt.id));

    this.snackbar.open(`Receipt was deleted!`, null, { duration: 3000 });

    await lastValueFrom(of(0).pipe(delay(500))); // index delete dosen't refresh that fast
    this.datasource.onSearch(this.filter);
  }

  openFilters() {
    this.filtersPanel()?.open();
  }

  onSearch(f: IReceiptQuery) {
    this.filter = f;
    this.currentPage = 1; // Reset to page 1 on new search
    this.datasource.onSearch(f);
  }

  // Pagination methods for UI pagination
  loadPage(page: number, pageSize: number = this.pageSize) {
    this.currentPage = page;
    const query = {
      ...this.filter,
      page: page,
      pageSize: pageSize
    };

    this.datasource.loadPage(query).subscribe({
      next: (response) => {
        if (response) {
          this.totalItems = response.total || 0;
          this.updatePagination();
        }
      },
      error: (error) => {
        console.error('Error loading page:', error);
      }
    });
  }

  private updatePagination() {
    if (this.totalItems > 0) {
      this.totalPages = Math.ceil(this.totalItems / this.pageSize);
    } else {
      this.totalPages = 1;
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.loadPage(this.currentPage + 1, this.pageSize);
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.loadPage(this.currentPage - 1, this.pageSize);
    }
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.loadPage(page, this.pageSize);
    }
  }

  getVisiblePages(): (number | string)[] {
    const visiblePages: (number | string)[] = [];
    const totalPages = this.totalPages;
    const current = this.currentPage;

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        visiblePages.push(i);
      }
    } else {
      visiblePages.push(1);

      if (current <= 4) {
        for (let i = 2; i <= 5; i++) {
          visiblePages.push(i);
        }
        visiblePages.push('...');
        visiblePages.push(totalPages);
      } else if (current >= totalPages - 3) {
        visiblePages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          visiblePages.push(i);
        }
      } else {
        visiblePages.push('...');
        for (let i = current - 1; i <= current + 1; i++) {
          visiblePages.push(i);
        }
        visiblePages.push('...');
        visiblePages.push(totalPages);
      }
    }

    return visiblePages;
  }

  private convertEventTypeToLabel(eventType: string): string {
    const eventTypeMap: { [key: string]: string } = {
      'EventPunchcardAlreadyCompleted': 'Punchcard completed',
      'EventReceiptManualReviewLimitExceeded': 'Review limit reached',
      'EventTimeLimitReached': 'Limit reached',
      'EventStampsLimitReached': 'Stamps limit reached',
      'EventPointsLimitReached': 'Points limit reached',
      'EventManualVerificationRejected': 'Rejected',
      'EventManualVerificationApproved': 'Approved',
      'EventManualVerificationRequested': 'Manual Review Requested',
      'EventAnyConsumption': 'unknown',
      'EventConsumption': 'unknown',
      'EventReceiptNoReward': 'unknown',
      'EventConsumptionMatch': 'unknown',
      'EventPointsReceived': 'unknown'
    };

    return eventTypeMap[eventType] || eventType;
  }

  private loadUnknownStages(receipts: Receipt[]): void {
    if (!receipts || receipts.length === 0) return;

    // Find receipts that need stage lookup
    const receiptsNeedingStage = receipts.filter(receipt => {
      const tags = receipt.tags ?? [];
      const stages = tags
        .map(tag => this.tagToStageMap[tag])
        .filter(stage => stage !== undefined);

      // Only load if no stage from tags and not already cached or loading
      return stages.length === 0 &&
             !this.stageCache.has(receipt.id) &&
             !this.loadingStages.has(receipt.id);
    });

    // Load stages for these receipts - use docId instead of receipt.id
    receiptsNeedingStage.forEach(receipt => {
      // Skip if receipt doesn't have a docId
      if (!receipt.docId) {
        console.warn('Receipt has no docId, cannot fetch user activity:', receipt.id);
        return;
      }

      this.loadingStages.add(receipt.id);

      // Use docId instead of receipt.id for the API call
      this.receiptApi.getReceiptDetailedInfo(receipt.docId).subscribe({
        next: (data) => {
          // The API returns all receipts with the same docId
          // Find the matching receipt by id
          const matchingReceiptDetail = data.receiptDetails?.find(
            detail => detail.receipt?.id === receipt.id
          );

          if (matchingReceiptDetail) {
            const userActivity = matchingReceiptDetail.userActivity;

            if (userActivity && userActivity.length > 0) {
              const lastActivity = userActivity[userActivity.length - 1];
              const eventType = lastActivity.eventType ?? 'unknown';
              // Convert event type to readable label
              const readableStage = this.convertEventTypeToLabel(eventType);
              this.stageCache.set(receipt.id, readableStage);
            }
          }

          this.loadingStages.delete(receipt.id);
        },
        error: (error) => {
          console.error('Error fetching user activity for receipt:', receipt.id, error);
          this.loadingStages.delete(receipt.id);
        }
      });
    });
  }

  getStage(receipt: Receipt): string {
    const tags = receipt.tags ?? [];

    const stages = tags
      .map(tag => this.tagToStageMap[tag])
      .filter(stage => stage !== undefined);

    const sortedStages = stages
      .sort((a, b) => this.customOrder.indexOf(a) - this.customOrder.indexOf(b));

    let stage = sortedStages.length > 0 ? sortedStages[0] : 'unknown';

    // If stage is unknown, check cache
    if (stage === 'unknown') {
      const cachedStage = this.stageCache.get(receipt.id);
      stage = cachedStage || 'unknown';
    }

    // If stage is unknown and manual reject tag present, return 'Rejected' immediately
    if (stage === 'unknown' && tags.includes(RECEIPT_TAGS.REPROCESS_MANUAL_REJECT_TAG)) {
      stage = 'Rejected';
    }

    const limitStageKeywords = [
      'review limit reached',
      'limit reached',
      'stamps limit reached',
      'points limit reached'
    ];
    const stageLower = stage.toLowerCase();
    const isLimitStage = limitStageKeywords.some(keyword => stageLower.includes(keyword));
    const badgeClass = isLimitStage ? 'stage-badge stage-badge-limit' : 'stage-badge';

    return `<div class="stage-cell-with-badge"><span class="${badgeClass}">${stage}</span></div>`;
  }

  // Get raw stage value for sorting (without HTML)
  getStageValue(receipt: Receipt): string {
    const tags = receipt.tags ?? [];

    const stages = tags
      .map(tag => this.tagToStageMap[tag])
      .filter(stage => stage !== undefined);

    const sortedStages = stages
      .sort((a, b) => this.customOrder.indexOf(a) - this.customOrder.indexOf(b));

    let stage = sortedStages.length > 0 ? sortedStages[0] : 'unknown';

    // If stage is unknown, check cache
    if (stage === 'unknown') {
      const cachedStage = this.stageCache.get(receipt.id);
      stage = cachedStage || 'unknown';
    }

    return stage;
  }

  getTags(receipt: Receipt): string {
    // Hide tags for group header rows; only child rows show tags
    if ((receipt as any)._isGroupHeader) {
      return '';
    }

    const allTags = receipt.tags ?? [];
    if (allTags.length === 0) {
      return '';
    }

    const relevantMatches = allTags.filter(tag => {
      const normalizedTag = tag.toLowerCase();
      return this.normalizedRelevantTagKeywords.some(keyword => normalizedTag.includes(keyword));
    });

    const tagsToShow = relevantMatches.length > 0 ? relevantMatches : allTags;

    // Determine which tags to render (up to 2) while ensuring they exist in the receipt
    const visibleTags: string[] = [];
    for (const tag of tagsToShow) {
      if (visibleTags.length >= 2) {
        break;
      }
      visibleTags.push(tag);
    }

    if (visibleTags.length < 2) {
      const fallbackTags = allTags.filter(tag => !visibleTags.includes(tag));
      for (const tag of fallbackTags) {
        if (visibleTags.length >= 2) {
          break;
        }
        visibleTags.push(tag);
      }
    }

    const remainingCount = Math.max(allTags.length - visibleTags.length, 0);

    let items = visibleTags.map(item => `<span class="tag-badge">${item}</span>`).join(' ');

    if (remainingCount > 0) {
      items += ` <span class="tag-count-text">(+${remainingCount})</span>`;
    }

    return `<div class="tags-cell-with-badges">${items}</div>`;
  }

  getWarnings(receipt: Receipt): string {
    const warnings = receipt.meta?.warnings;
    return warnings?.length
      ? warnings.map(item => `<i>${item.message}</i>`).join('. ')
      : '';
  }

  getDecision(receipt: Receipt): string {
    const tags = receipt.tags ?? [];
    const fraudDecision = receipt.meta?.fraud?.decision;

    // Check for specific tags that indicate different decisions
    const hasManualAccept = tags.includes(RECEIPT_TAGS.REPROCESS_MANUAL_ACCEPT_TAG);
    const hasManualReject = tags.includes(RECEIPT_TAGS.REPROCESS_MANUAL_REJECT_TAG);
    const hasToCheckFraud = tags.includes(RECEIPT_TAGS.REPROCESS_CHECK_FRAUD);
    const hasToManualVerify = tags.includes(RECEIPT_TAGS.MANUAL_VERIFY_TAG);
    const hasReceivedStamps = tags.includes(RECEIPT_TAGS.STAMPS_RECEIVED_TAG);
    const hasReceivedPoints = tags.includes(RECEIPT_TAGS.POINTS_RECEIVED_TAG);

    // Check for punchcard completed (case insensitive)
    const hasPunchcardCompleted = tags.some(tag => tag.toLowerCase().includes('punchcardcompleted'));

    // Check for limitation tags (not shown in receipt BO yet)
    const hasLimitationTag = tags.some(tag =>
      tag.toLowerCase().includes('limitation') ||
      tag.toLowerCase().includes('receipt-review')
    );

    let decision = '';

    // CLEARED: eligible receipt or rejected for any reason
    if (hasManualAccept || hasReceivedStamps || hasReceivedPoints) {
      decision = 'Cleared';
    }
    // FRAUD: not eligible receipt
    else if (hasManualReject || hasToCheckFraud) {
      decision = 'Fraud';
    }
    // INELIGIBLE: user pushed for check, but limit reached
    else if (hasPunchcardCompleted) {
      decision = 'Ineligible';
    }
    // CLEARED: limitation receipt-review (treated as cleared/rejected)
    else if (hasLimitationTag) {
      decision = 'Cleared';
    }
    // PENDING: pending for review (for items not touched yet)
    else if (hasToManualVerify) {
      decision = 'Pending';
    }
    // Check fraud decision from meta
    else if (fraudDecision) {
      decision = fraudDecision === 'fraud' ? 'Fraud' : 'Cleared';
    }
    // Default for receipts not touched yet
    else {
      decision = 'Pending';
    }

    return decision;
  }

  getDecisionWithStyling(receipt: Receipt): string {
    const decision = this.getDecision(receipt);
    return `<div class="decision-cell-with-badge"><span class="decision-badge decision-${decision.toLowerCase()}">${decision}</span></div>`;
  }

  getDateWithNewIndicator(receipt: Receipt): string {
    const createDate = receipt.createDate;
    if (!createDate) {
      return '';
    }

    // Format the date (using Angular's date pipe format)
    const formattedDate = new Date(createDate).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    // Add blue dot if receipt is new
    if (receipt.isNew) {
      return `<div class="date-cell-with-new"><div class="new-status-dot"></div><span>${formattedDate}</span></div>`;
    }

    return formattedDate;
  }

  highlightText(text: string | null | undefined): string {
    if (!text) {
      return '';
    }

    const searchText = this.datasource.searchText?.trim();
    if (!searchText) {
      return text;
    }

    const regex = new RegExp(`(${this.escapeRegex(searchText)})`, 'gi');
    return text.replace(regex, '<span class="highlight-text">$1</span>');
  }

  private escapeRegex(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  checkPermissions(permissions: string[]): boolean {
    let hasPermission = false;
    this.userPermissionsService.hasAnyPermission(permissions).subscribe(value => {
      hasPermission = value;
    });
    return hasPermission;
  }

  onActivate(e: any) {
    // Skip activation for child rows
    if (e._isGroupChild) {
      return;
    }

    this.selectedReceipt = e;
    if (!this.selectedReceipt) {
      return;
    }

    if (this.selectedReceipt.isNew) {
      this.markReceiptAsRead(this.selectedReceipt);
    }
    this.detailPanel()?.open(this.selectedReceipt);

    // Add active class to the row - find the group header or single row
    let displayIndex = -1;
    if (e._isGroupHeader) {
      // If it's already a group header, find its index directly
      displayIndex = this.displayReceipts.findIndex(r => {
        const row = r as any;
        return row.id === this.selectedReceipt.id && row._isGroupHeader;
      });
    } else {
      // For single rows (not grouped), just find the row
      displayIndex = this.displayReceipts.findIndex(r => {
        const row = r as any;
        return row.id === this.selectedReceipt.id && !row._isGroupChild;
      });
    }

    if (displayIndex !== -1) {
      this.setActiveRowByDisplayIndex(displayIndex);
    } else {
      console.warn('Could not find display index for receipt:', this.selectedReceipt.id);
    }
  }

  // Method to select receipt by index (for keyboard navigation)
  selectReceiptByIndex(index: number) {
    // Use navigableRows which only contains group headers and single rows
    if (index >= 0 && index < this.navigableRows.length) {
      const receipt = this.navigableRows[index];

      // Find the corresponding row in displayReceipts
      const displayIndex = this.displayReceipts.findIndex(r => {
        const row = r as any;
        const targetRow = receipt as any;
        // Match by ID and same row type (both group headers or both non-children)
        return row.id === receipt.id &&
               ((row._isGroupHeader && targetRow._isGroupHeader) ||
                (!row._isGroupHeader && !targetRow._isGroupHeader && !row._isGroupChild));
      });

      this.selectedReceipt = receipt;
      this.detailPanel()?.open(receipt);

      // Set active on the display row (group header)
      if (displayIndex !== -1) {
        this.setActiveRowByDisplayIndex(displayIndex);
      } else {
        console.warn('Could not find display row for navigable index:', index, 'ID:', receipt.id);
      }

      if (receipt.isNew) {
        this.markReceiptAsRead(receipt);
      }
    } else {
      console.warn('Invalid navigation index:', index, 'navigableRows length:', this.navigableRows.length);
    }
  }

  // Add active class to selected row by display index
  private setActiveRowByDisplayIndex(displayIndex: number) {
    if (displayIndex < 0 || displayIndex >= this.displayReceipts.length) {
      console.warn('Invalid displayIndex:', displayIndex);
      return;
    }

    requestAnimationFrame(() => {
      // Remove active class from all rows
      const allRows = document.querySelectorAll('.datatable-body-row');
      allRows.forEach(row => row.classList.remove('active'));

      // Get all visible rows in the DOM
      const rows = Array.from(document.querySelectorAll('.datatable-body-row'));

      // The DOM rows should match displayReceipts order
      // Just use the displayIndex directly
      let activeRow: HTMLElement | null = null;

      if (displayIndex >= 0 && displayIndex < rows.length) {
        activeRow = rows[displayIndex] as HTMLElement;
      } else {
        console.warn('DisplayIndex out of bounds:', displayIndex, 'DOM rows count:', rows.length);
      }

      if (activeRow) {
        activeRow.classList.add('active');
        activeRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        console.warn('Could not find row element for display index:', displayIndex);
      }
    });
  }

  private markReceiptAsRead(receipt: Receipt): void {
    // Update locally first for immediate UI feedback
    receipt.isNew = false;

    // Update in main receipts list
    const listIndex = this.receiptsList.findIndex(r => r.id === receipt.id);
    if (listIndex !== -1) {
      this.receiptsList[listIndex].isNew = false;
    }

    // Update in grouped receipts map
    this.groupedReceipts.forEach((receipts) => {
      const groupIndex = receipts.findIndex(r => r.id === receipt.id);
      if (groupIndex !== -1) {
        receipts[groupIndex].isNew = false;
      }
    });

    // Rebuild display receipts to reflect the change
    this.groupReceiptsByDocId();

    // Force datatable refresh to show updated blue dot status
    const dt = this.datatable();
    if (dt) {
      dt.rows = [];
      setTimeout(() => {
        dt.rows = this.displayReceipts;
        dt.recalculate();
      }, 10);
    }

    // Persist to backend by calling getReceiptById which automatically sets isNew = false
    // This uses the existing backend logic in GetById endpoint
    this.receiptApi.getReceiptById(receipt.id).subscribe({
      next: () => {
      },
      error: (error) => {
        console.error('Error marking receipt as read:', error);
        // Revert local changes if backend update fails
        receipt.isNew = true;

        if (listIndex !== -1) {
          this.receiptsList[listIndex].isNew = true;
        }

        // Update in grouped receipts map
        this.groupedReceipts.forEach((receipts) => {
          const groupIndex = receipts.findIndex(r => r.id === receipt.id);
          if (groupIndex !== -1) {
            receipts[groupIndex].isNew = true;
          }
        });

        // Rebuild display after reverting
        this.groupReceiptsByDocId();

        const dt = this.datatable();
        if (dt) {
          dt.rows = [];
          setTimeout(() => {
            dt.rows = this.displayReceipts;
            dt.recalculate();
          }, 10);
        }
      }
    });
  }
}

